<template>
  <div class="container-fluid">
    <div class="bg-wheel py-3 position-relative">
      <!-- 标题 & 计时 -->
      <h1 class="text-white text-center" style="font-family: 'SSGLT'">
        🎮 卡片翻翻乐 🎮
      </h1>
      <div class="score-board text-white p-3 rounded my-3 text-center">
        计时: <span>{{ elapsed }}s</span>
      </div>

      <!-- 开始按钮 -->
      <div class="controls d-flex flex-wrap gap-3 justify-content-center my-3">
        <button
          class="btn btn-success btn-lg"
          :disabled="playing"
          @click="startGame"
        >
          {{ playing ? '进行中…' : '开始游戏' }}
        </button>
      </div>

      <!-- 牌组网格 -->
      <div class="game-container">
        <div
          class="card"
          v-for="(card, idx) in board"
          :key="idx"
        >
          <div
            class="card-wrapper"
            :class="{ flipped: flipped.includes(idx) }"
            @click="flip(idx)"
          >
            <div class="card-inner">
              <div class="card-front">{{ idx + 1 }}</div>
              <div class="card-back">
                <div class="emoji">{{ card.emoji }}</div>
                <div>{{ card.name }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 成功提示 -->
      <div
        v-if="matched === 8"
        class="position-absolute top-50 start-50 translate-middle bg-dark p-4 rounded text-center"
      >
        <h2 style="color: white">🎉 恭喜挑战成功！ 🎉</h2>
        <button class="btn btn-success" @click="restartGame">重新挑战</button>
      </div>

      <!-- 底部装饰 -->
      <img
        src="@/assets/images/Nezha2.png"
        class="corner-decoration bottom-0 start-0"
      />
      <img
        src="@/assets/images/Nezha2.png"
        class="corner-decoration bottom-0 end-0 image-flip"
      />
    </div>
  </div>
</template>

<style scoped>
@import "@/assets/font/font.css"; /* 引入字体样式 */
.bg-wheel {
  background: #8a030a; /* 与单词大作战相同的背景色 */
  min-height: 800px;
}

.game-container {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 15px;
  max-width: 800px;
  margin: 20px auto;
  padding: 0 20px;
}

.card {
  width: 100%;
  max-width: 150px;
  height: 200px;
  border-radius: .75rem !important;
  overflow: hidden; /* ensure child faces don’t leak past the corners */
}

.card-wrapper {
  perspective: 1000px;
  cursor: pointer;
  width: 100%;
  height: 100%;
}

.card-inner {
  position: relative;
  width: 100%;
  height: 100%;
  transition: transform 0.6s;
  transform-style: preserve-3d;
}

.flipped .card-inner {
  transform: rotateY(180deg);
}


.card-front,
.card-back {
  position: absolute;
  inset: 0;
  backface-visibility: hidden;
  border-radius: .75rem; /* 统一设置相同的圆角 */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.card-front {
  background: #FFD700; /* 黄色卡片的背景色 */
  color: #FFF;
  font-size: 3em;
}

.card-back {
  background: #FFF; /* 白色卡片的背景色 */
  transform: rotateY(180deg);
  padding: 10px;
}

.emoji {
  font-size: 4em;
  margin-bottom: 10px;
}

/* 底部装饰的样式保持一致 */
.corner-decoration {
  position: absolute;
  width: 120px;
  z-index: 100;
}

/* 游戏结束提示区域的样式保持一致 */
.position-absolute.top-50.start-50.translate-middle.bg-dark.p-4.rounded.text-center {
  background-color: rgba(0, 0, 0, 0.8) !important; /* 确保背景色一致 */
}

/* 其他样式保持一致，例如按钮样式等 */
.btn.btn-success {
  background-color: #4CAF50 !important; /* 确保按钮颜色一致 */
  border-color: #4CAF50 !important;
}
</style>



  <script setup>
  import { ref, watch, onBeforeUnmount } from 'vue'
  
  // 全部 20 种动植物
  const items = [
    { name: '熊猫', emoji: '🐼' }, { name: '狮子', emoji: '🦁' },
    { name: '兔子', emoji: '🐰' }, { name: '考拉', emoji: '🐨' },
    { name: '松鼠', emoji: '🐿️' }, { name: '向日葵', emoji: '🌻' },
    { name: '仙人掌', emoji: '🌵' }, { name: '枫叶', emoji: '🍁' },
    { name: '樱花', emoji: '🌸' }, { name: '蘑菇', emoji: '🍄' },
    { name: '海豚', emoji: '🐬' }, { name: '企鹅', emoji: '🐧' },
    { name: '独角兽', emoji: '🦄' }, { name: '椰树', emoji: '🌴' },
    { name: '玫瑰', emoji: '🌹' }, { name: '四叶草', emoji: '🍀' },
    { name: '竹子', emoji: '🎍' }, { name: '苹果', emoji: '🍎' },
    { name: '蜜蜂', emoji: '🐝' }, { name: '蝴蝶', emoji: '🦋' }
  ]
  
  const board      = ref([])
  const flipped    = ref([])
  const matched    = ref(0)
  const elapsed    = ref(0)
  const playing    = ref(false)
  const lockBoard  = ref(false)
  let timerId      = null
  const audioCtx   = new (window.AudioContext||window.webkitAudioContext)()
  
  // 随机打乱
  function shuffle(arr) {
    return arr.slice().sort(() => Math.random() - 0.5)
  }
  
  // 游戏开始
  function startGame() {
    playing.value = true
    matched.value  = 0
    flipped.value  = []
    elapsed.value  = 0
    board.value    = shuffle([
      ...shuffle(items).slice(0,8),
      ...shuffle(items).slice(0,8)
    ])
    // 先从 items 随机抽 8 个
   const selected = shuffle(items).slice(0, 8)
board.value    = shuffle([
     ...selected,
     ...selected
   ])
    clearInterval(timerId)
    timerId = setInterval(() => elapsed.value++, 1000)
  }
  
  // 翻牌逻辑
  function flip(idx) {
    if (!playing.value || lockBoard.value || flipped.value.includes(idx)) return
    flipped.value.push(idx)
    if (flipped.value.length % 2 === 0) {
      lockBoard.value = true
      checkMatch()
    } else {
      playClick()
    }
  }
  
  // 配对检查
  function checkMatch() {
    const [i1, i2] = flipped.value.slice(-2)
    if (board.value[i1].name === board.value[i2].name) {
      matched.value++
      playSuccess()
      lockBoard.value = false
      if (matched.value === 8) endGame()
    } else {
      setTimeout(() => {
        flipped.value.splice(-2, 2)
        lockBoard.value = false
      }, 1000)
    }
  }
  
  // 结束逻辑
  function endGame() {
    clearInterval(timerId)
    playCelebrate()
    playing.value = false
  }
  
  function playClick() {
    const osc = audioCtx.createOscillator(), gain = audioCtx.createGain()
    osc.type='sine'; osc.frequency.setValueAtTime(600,audioCtx.currentTime)
    gain.gain.setValueAtTime(0.05,audioCtx.currentTime)
    osc.connect(gain).connect(audioCtx.destination)
    osc.start(); osc.stop(audioCtx.currentTime+0.1)
  }
  
  function playSuccess() {
    const osc = audioCtx.createOscillator(), gain = audioCtx.createGain()
    osc.type='square'; osc.frequency.setValueAtTime(880,audioCtx.currentTime)
    gain.gain.setValueAtTime(0.1,audioCtx.currentTime)
    osc.connect(gain).connect(audioCtx.destination)
    osc.start(); osc.stop(audioCtx.currentTime+0.3)
  }
  
  function playCelebrate() {
    const osc = audioCtx.createOscillator(), gain = audioCtx.createGain()
    osc.type='square'; osc.frequency.setValueAtTime(440,audioCtx.currentTime)
    gain.gain.setValueAtTime(0.1,audioCtx.currentTime)
    osc.connect(gain).connect(audioCtx.destination)
    osc.start()
    osc.frequency.exponentialRampToValueAtTime(1760,audioCtx.currentTime+1)
    gain.gain.exponentialRampToValueAtTime(0.01,audioCtx.currentTime+1.5)
    osc.stop(audioCtx.currentTime+1.5)
  }
  
  onBeforeUnmount(() => clearInterval(timerId))
  </script>
  

  